import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { toast } from '@ooh/ui/components/sonner';
import { ApiErrorCode, parseApiError } from '@ooh/contracts';
import { useDeleteMediaUnit } from './api/mutations';

interface DeleteMediaUnitDialogProps {
  isOpen: boolean;
  mediaUnitId: number;
  grwId: string;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeleteMediaUnitDialog({
  isOpen,
  mediaUnitId,
  grwId,
  onClose,
  onDeleted,
}: DeleteMediaUnitDialogProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <DeleteMediaUnitDialogContent
      mediaUnitId={mediaUnitId}
      grwId={grwId}
      onClose={onClose}
      onDeleted={onDeleted}
    />
  );
}

function DeleteMediaUnitDialogContent({
  mediaUnitId,
  grwId,
  onClose,
  onDeleted,
}: Omit<DeleteMediaUnitDialogProps, 'isOpen'>) {
  const { t } = useTranslation('inventory');
  const deleteMediaUnit = useDeleteMediaUnit();

  async function handleConfirm() {
    try {
      await deleteMediaUnit.mutateAsync({ id: mediaUnitId });
      onDeleted();
      toast.success(t('detail.deleteSuccess'));
    } catch (error: unknown) {
      const parsed = parseApiError(error);
      if (parsed.errorCode === ApiErrorCode.MEDIA_UNIT_REFERENCED_BY_DRAFT_OFFER) {
        toast.error(t('detail.deleteBlockedByDraftOffer'));
      } else {
        toast.error(t('detail.deleteError'));
      }
      onClose();
    }
  }

  return (
    <ModalDialog
      closeLabel={t('detail.deleteDialogCancel')}
      header={<h2 className="text-base font-semibold">{t('detail.deleteDialogTitle')}</h2>}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('detail.deleteDialogCancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={deleteMediaUnit.isPending}
            onClick={() => void handleConfirm()}
          >
            {t('detail.deleteDialogConfirm')}
          </Button>
        </>
      }
      onClose={onClose}
      testId="delete-media-unit-dialog"
    >
      <p className="text-sm text-muted-foreground">{t('detail.deleteConfirm', { grwId })}</p>
    </ModalDialog>
  );
}
